import style from './burger-menu.module.scss';
import { HeaderNavigation, HeaderState } from '../header.state';
import BurgerMenuNavItem from './burger-menu-nav-item/burger-menu-nav-item';
import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { Themes } from '@layouts/main-layout/themes.enum';
import Link from 'next/link';
import Image from 'next/legacy/image';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { ReactSVG } from 'react-svg';

const StartProjectModal = dynamic(
  () => import('@components/shared/interactions/start-project/start-project.modal'),
);

interface IBurgerMenuProps {
  isOpened: boolean;
  onMenuClicked: () => void;
  theme?: Themes;
}

export default function BurgerMenu({ isOpened, onMenuClicked, theme }: IBurgerMenuProps) {
  const [isStartProjectModalOpened, setStartProjectModalOpened] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const t = useTranslations();
  const router = useRouter();
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (isOpened) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    const handleRouteChange = () => {
      document.body.style.overflow = '';
    };
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      document.body.style.overflow = '';
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [isOpened, router.events]);

  const openStartProjectWindow = () => {
    setStartProjectModalOpened(true);
    onMenuClicked();
  };
  const { navigation } = HeaderState;

  const actionButton = navigation.find((item: HeaderNavigation) => item.actionButton);

  if (!theme) {
    theme = Themes.Default;
  }

  return (
    <>
      {/* Burger Menu for Mobile version*/}
      <div className={`${style.menu} ${style[theme]} ${isOpened ? `${style.menu_active}` : null}`}>
        <div className={`${style.menu__block} flex flex-col h-full`}>
          <div className={`${style.menu__nav}`}>
            {navigation.map((item: HeaderNavigation, index: number) => {
              if (!item.actionButton) {
                return (
                  <BurgerMenuNavItem
                    ref={el => { itemRefs.current[index] = el; }}
                    theme={theme}
                    onMenuClicked={onMenuClicked}
                    key={index}
                    item={item}
                    active={activeIndex === index}
                    onExpand={() => {
                      setActiveIndex(activeIndex === index ? null : index);
                      setTimeout(() => {
                        if (itemRefs.current[index]) {
                          itemRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 0);
                    }}
                  />
                );
              }
            })}
          </div>
          <div className='flex-1 h-full'></div>
          {actionButton && (
            <div className='sticky bottom-0 left-0 right-0 bg-white'>
              <Link href={actionButton.link} className='flex items-center relative' key={actionButton.name} onClick={() => onMenuClicked()}>
                <div className={`${style.icon_container} flex items-center justify-center gap-2 group cursor-pointer border-t border-gray-100 py-5 w-full`}>
                  {actionButton.Icon && <actionButton.Icon className="w-5 h-5 filter-primary-800-hover" />}
                  <span className='text-primary-600 group-hover:text-primary-800 font-semibold'>{t(actionButton.name)}</span>
                </div>
              </Link>
            </div>
          )}
        </div>
      </div>
      {/* ====== Modals ====== */}
      <StartProjectModal
        isOpen={isStartProjectModalOpened}
        onClose={() => setStartProjectModalOpened(false)}
      />
    </>
  );
}
